import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Heart, Clock } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { PayPalButtons } from '@paypal/react-paypal-js';

export default function SupportChronosCard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [loading, setLoading] = useState(false);

  const donationAmounts = [
    { value: 100, label: '£1' },
    { value: 500, label: '£5' },
    { value: 1000, label: '£10' },
  ];

  const createDonationOrder = async (amount) => {
    try {
      const response = await axios.post(
        `${API}/donations/monetary`,
        { amount },
        { withCredentials: true }
      );
      return response.data.order_id;
    } catch (error) {
      console.error('Error creating donation order:', error);
      toast.error(t('errors.paymentFailed'));
      return null;
    }
  };

  const handleApprove = async (data) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/donations/monetary/capture?order_id=${data.orderID}`,
        {},
        { withCredentials: true }
      );
      
      toast.success('❤️ ' + t('donate.thankYou'), {
        description: t('donate.donationSuccess', { amount: `£${response.data.amount_donated.toFixed(2)}` })
      });
      
      setShowPayPal(false);
      setSelectedAmount(null);
    } catch (error) {
      console.error('Error capturing donation:', error);
      toast.error(t('errors.paymentFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 border-2 border-rose-200 shadow-md">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative p-3 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full shadow-lg">
          <Heart className="h-6 w-6 text-white fill-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">{t('donate.supportChronos')}</h3>
          <p className="text-sm text-rose-600">{t('donate.supportSubtitle')}</p>
        </div>
      </div>

      {!showPayPal ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 mb-4">
            {t('donate.helpPlatform')}
          </p>
          
          <div className="grid grid-cols-3 gap-3">
            {donationAmounts.map((amount) => (
              <Button
                key={amount.value}
                onClick={() => {
                  setSelectedAmount(amount.value);
                  setShowPayPal(true);
                }}
                variant="outline"
                className="border-2 border-rose-300 text-rose-600 hover:bg-rose-50 hover:border-rose-400 font-bold"
              >
                {amount.label}
              </Button>
            ))}
          </div>
          
          {user?.monetary_donations > 0 && (
            <div className="mt-4 p-3 bg-white rounded-lg border border-rose-200 text-center">
              <p className="text-xs text-gray-600">{t('donate.totalDonated')}</p>
              <p className="text-lg font-bold text-rose-600">
                £{(user.monetary_donations / 100).toFixed(2)}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-lg border-2 border-rose-200">
            <p className="text-center text-lg font-bold text-gray-900 mb-2">
              {t('donate.donating')} {donationAmounts.find(a => a.value === selectedAmount)?.label}
            </p>
            <PayPalButtons
              style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'donate' }}
              createOrder={async () => await createDonationOrder(selectedAmount)}
              onApprove={handleApprove}
              onError={(err) => {
                console.error('PayPal error:', err);
                toast.error(t('errors.paymentFailed'));
              }}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setShowPayPal(false);
              setSelectedAmount(null);
            }}
            className="w-full"
          >
            {t('common.cancel')}
          </Button>
        </div>
      )}
    </Card>
  );
}
